import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightbulb, Plus, Scale } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, errMsg, has } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/app/PageHeader";
import { StatusBadge } from "../components/app/PlanForm";

const AREAS = { spiritual: "Spiritual and Faith Formation", family: "Family and Community Welfare", health: "Health and Human Development" };
const LEVEL = { 1: "Low", 2: "Medium", 3: "High" };

const Level = ({ k, form, setForm }) => (
  <label className="block space-y-1"><span className="text-xs font-semibold capitalize text-stone-600">{k}</span>
    <Select value={String(form[k])} onValueChange={(v) => setForm({ ...form, [k]: Number(v) })}>
      <SelectTrigger className="bg-white" data-testid={`priority-${k}-select`}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white">{[1, 2, 3].map((n) => <SelectItem key={n} value={String(n)}>{LEVEL[n]}</SelectItem>)}</SelectContent>
    </Select>
  </label>
);

const ProposeDialog = ({ form, setForm, onSave }) => (
  <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
    <DialogContent className="bg-white max-w-xl">
      <DialogHeader><DialogTitle>Propose a pastoral priority</DialogTitle>
        <DialogDescription>Your pastoral judgment matters here. Severity, urgency and feasibility are your assessment, not the system's.</DialogDescription></DialogHeader>
      {form && (
        <div className="space-y-3">
          {form.evidence && <p className="rounded-lg bg-[#F5F0E6] p-3 text-sm text-stone-700" data-testid="priority-evidence">{form.evidence}</p>}
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Pastoral concern" data-testid="priority-title-input" />
          <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
            <SelectTrigger className="bg-white" data-testid="priority-area-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{Object.entries(AREAS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Why this matters, what leaders have observed…" data-testid="priority-description-input" />
          <div className="grid grid-cols-3 gap-3">{["severity", "urgency", "feasibility"].map((k) => <Level key={k} k={k} form={form} setForm={setForm} />)}</div>
        </div>
      )}
      <DialogFooter><Button onClick={onSave} className="bg-amber-900 hover:bg-[#5B210B]" data-testid="priority-save-button">Submit proposal</Button></DialogFooter>
    </DialogContent>
  </Dialog>
);

const DecisionDialog = ({ dec, setDec, onSave }) => (
  <Dialog open={!!dec} onOpenChange={(o) => !o && setDec(null)}>
    <DialogContent className="bg-white">
      <DialogHeader><DialogTitle>Record decision: {dec?.decision}</DialogTitle><DialogDescription>{dec?.p.title}</DialogDescription></DialogHeader>
      <Textarea rows={3} placeholder={dec?.decision === "approved" ? "Note (optional)" : "Reason (required)"} value={dec?.note || ""} onChange={(e) => setDec({ ...dec, note: e.target.value })} data-testid="decision-note-input" />
      <DialogFooter><Button onClick={onSave} className="bg-amber-900 hover:bg-[#5B210B]" data-testid="decision-save-button">Confirm</Button></DialogFooter>
    </DialogContent>
  </Dialog>
);

const Suggestions = ({ data, onPropose }) => (
  <section className="rounded-xl border border-amber-700/15 bg-white p-5 sm:p-6" data-testid="suggestions-panel">
    <p className="flex items-center gap-2 font-semibold text-stone-800"><Lightbulb className="h-4 w-4 text-[#285E3B]" /> Findings that may deserve attention · survey period {data.period || "—"}</p>
    <p className="mt-1 text-xs text-stone-500">{data.note}</p>
    <div className="mt-4 grid gap-3 md:grid-cols-2">
      {data.suggestions.map((s) => (
        <div key={s.rule_key} className="rounded-lg border border-amber-700/10 p-4" data-testid={`suggestion-${s.rule_key}`}>
          <p className="text-[10px] uppercase tracking-widest font-semibold text-amber-800/80">{s.area_label}</p>
          <p className="mt-1 font-serif-display text-lg font-semibold text-amber-950">{s.title}</p>
          <p className="mt-1 text-sm text-stone-600">{s.evidence}</p>
          <p className="mt-1 text-xs text-stone-500">{s.community_expressed ? "Expressed directly by households" : "Observed from survey answers"}</p>
          {s.already_proposed ? <p className="mt-3 text-xs font-semibold text-[#285E3B]">Already proposed</p>
            : <Button size="sm" variant="outline" className="mt-3" onClick={() => onPropose(s)} data-testid={`propose-${s.rule_key}`}>Propose as priority</Button>}
        </div>
      ))}
      {!data.suggestions.length && <p className="text-sm text-stone-500">No findings with enough responses yet.</p>}
    </div>
  </section>
);

const PriorityRow = ({ p, canDecide, onDecide }) => (
  <div className="rounded-xl border border-amber-700/15 bg-white p-5" data-testid={`priority-${p.priority_id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-[10px] uppercase tracking-widest font-semibold text-amber-800/80">{p.area_label} · {p.gkk_name}</p>
        <p className="mt-1 font-serif-display text-xl font-semibold text-amber-950">{p.title}</p>
      </div>
      <StatusBadge s={p.status} testId={`priority-status-${p.priority_id}`} />
    </div>
    {p.evidence && <p className="mt-2 text-sm text-stone-700"><b>Evidence:</b> {p.evidence.evidence}</p>}
    {p.description && <p className="mt-1 text-sm text-stone-600">{p.description}</p>}
    <p className="mt-2 flex items-center gap-2 text-xs text-stone-500"><Scale className="h-3 w-3" /> Severity {LEVEL[p.severity]} · Urgency {LEVEL[p.urgency]} · Feasibility {LEVEL[p.feasibility]} · proposed by {p.created_by_name}</p>
    {p.decision_note && <p className="mt-1 text-xs text-stone-600">Decision note: {p.decision_note} ({p.decided_by})</p>}
    <div className="mt-3 flex flex-wrap gap-2">
      {canDecide && p.status !== "approved" && <Button size="sm" className="bg-[#285E3B] hover:bg-[#1E462C]" onClick={() => onDecide(p, "approved")} data-testid={`approve-priority-${p.priority_id}`}>Approve</Button>}
      {canDecide && p.status === "proposed" && <Button size="sm" variant="outline" onClick={() => onDecide(p, "deferred")} data-testid={`defer-priority-${p.priority_id}`}>Defer</Button>}
      {canDecide && p.status === "proposed" && <Button size="sm" variant="outline" onClick={() => onDecide(p, "declined")} data-testid={`decline-priority-${p.priority_id}`}>Decline</Button>}
      {p.status === "approved" && <Link to={`/plans?new=${p.priority_id}`}><Button size="sm" className="bg-amber-900 hover:bg-[#5B210B]" data-testid={`create-plan-${p.priority_id}`}>Create action plan</Button></Link>}
      {p.plans > 0 && <Link to="/plans" className="text-xs self-center text-amber-900 underline">{p.plans} action plan(s)</Link>}
    </div>
  </div>
);

export default function Priorities() {
  const { user } = useAuth();
  const [sugg, setSugg] = useState(null);
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [dec, setDec] = useState(null);
  const canSee = has(user, "analytics:all", "analytics:own_gkk");
  const load = () => {
    api.get("/priorities").then((r) => setList(r.data));
    if (canSee) api.get("/priorities/suggestions").then((r) => setSugg(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const propose = (s) => setForm({ title: s.title, area: s.area, description: "", rule_key: s.rule_key, period: s.period, evidence: s.evidence, severity: 2, urgency: 2, feasibility: 2 });
  const save = async () => {
    try { const { evidence, ...body } = form; await api.post("/priorities", body); toast.success("Priority proposed"); setForm(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const decide = async () => {
    try { await api.post(`/priorities/${dec.p.priority_id}/decision`, { decision: dec.decision, note: dec.note || "" }); toast.success("Decision recorded"); setDec(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Pastoral discernment" title="Priority Pastoral Concerns" testId="priorities-header">
        The system points to findings; the Parish Priest and pastoral leaders decide. No suggestion here determines who receives assistance.
      </PageHeader>
      <Button onClick={() => setForm({ title: "", area: "spiritual", description: "", severity: 2, urgency: 2, feasibility: 2 })} className="bg-amber-900 hover:bg-[#5B210B]" data-testid="new-priority-button"><Plus className="h-4 w-4 mr-2" />Propose a priority</Button>
      {sugg && <Suggestions data={sugg} onPropose={propose} />}
      <section className="space-y-3" data-testid="priorities-list">
        <h2 className="font-serif-display text-2xl font-semibold text-amber-900">Proposed and decided priorities</h2>
        {list.map((p) => <PriorityRow key={p.priority_id} p={p} canDecide={has(user, "priority:decide")} onDecide={(pp, d) => setDec({ p: pp, decision: d, note: "" })} />)}
        {!list.length && <p className="text-sm text-stone-500">No priorities proposed yet.</p>}
      </section>
      <ProposeDialog form={form} setForm={setForm} onSave={save} />
      <DecisionDialog dec={dec} setDec={setDec} onSave={decide} />
    </div>
  );
}
